import {React, useEffect, useState} from 'react';
import { Button, message, Steps, theme } from 'antd';
import ChooseModel from "./ChooseModel";
import TextureDescription from "./TextureDescription";
import TexturingResults from "./TexturingResults";
import {useAuthState} from "react-firebase-hooks/auth";
import {auth, default3DAssetsStorage, resultsStorage} from "../../tools/firebase";
import {track} from "@amplitude/analytics-browser";
import {useNavigate} from "react-router-dom";
import DashboardHeader from "../DashboardHeader";
import axios from "axios";
import {GLOBAL_CONFIG} from "../../tools/global_config";
import {getDownloadURL, ref as sRef} from "firebase/storage";

const TexturingContainer = ({isVolumetric, isForRaf}) => {
    const navigate = useNavigate();
    const [user, loading, error] = useAuthState(auth);
    const [guid, setGuid] = useState("");
    const [name, setName] = useState("");
    const [prompt, setPrompt] = useState("");
    const [current, setCurrent] = useState(0);
    const [token, setToken] = useState("");
    const [bucketPath, setBucketPath] = useState(isVolumetric ? "stylized_chest.glb" : "axe_test.glb");
    const [assetUrl, setAssetUrl] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [results, setResults] = useState({});
    const [generationTime, setGenerationTime] = useState(0);
    const [progress, setProgress] = useState(0);

    const [rotForDescription, setRotForDescription] = useState(isVolumetric ? [Math.PI / 2, Math.PI, 0] : [Math.PI, Math.PI / 2, -Math.PI / 2] );
    const [camForDescription, setCamForDescription] = useState(isVolumetric ? { position: [-1, 20, 20], fov: 10 } : { position: [-10, 15, 25], fov: 5 });

    const onAssetUploaded = (newAssetUrl, rotation, camera) => {
        setAssetUrl(newAssetUrl);
        setCamForDescription(camera);
        setRotForDescription(rotation);
        setCurrent(1);
    }

    const fetchToken = async () => {
        try {
            let newToken = await user?.getIdToken();
            setToken(newToken);
            return newToken;
        } catch (err) {
            console.error(err);
            alert(err);
        }
    }

    const extractResultDownloadUrl = (result_path) => {
        let url = getDownloadURL(sRef(resultsStorage, result_path));
        return url
    }

    var FakeProgress = require("fake-progress");

    const onGenerateTap = async (prompt) => {
        //track('boost_prompt_clicked', {'init_text' : prompt});
        let newToken = await fetchToken();
        var t0 = performance.now();
        var p = new FakeProgress({
            timeConstant : isVolumetric ? 20000 : 8000,
            autoStart : true
        });
        setGenerating(true);
        setPrompt(prompt);

        var onEachSecond = function(){
            setProgress(p.progress);
        };

        var interval = setInterval(onEachSecond, 100);

        const reqPath = isVolumetric ? "stylized_chest.glb" : "axe_test.glb";

        let config = {
            headers: {
                'Authorization': `Bearer ${newToken}`
            },
            params: {
                input_model_blob_path: reqPath,
                prompt: prompt
            }
        }

        try {
            const response = await axios.get(
                isVolumetric ? GLOBAL_CONFIG.texturing_3d_volumetric_url : GLOBAL_CONFIG.texturing_3d_url,
                config
            );
            let results = [];
            for (let key in response.data) {
                let item = response.data[key];
                let url = await extractResultDownloadUrl(item.output_path);
                results.push(url);
            }
            setResults(results);
            var t1 = performance.now();
            p.end();
            clearInterval(interval);
            setGenerationTime(t1 - t0);

            function timeout(delay) {
                return new Promise( res => setTimeout(res, delay) );
            }

            setProgress(1);
            await timeout(500);
            setGenerating(false);
            setCurrent(isVolumetric || isForRaf ? 1 : 2);
            setProgress(0);
        } catch (e) {
            setGenerating(false);
            p.end();
            clearInterval(interval);
            setProgress(0);
            message.error("Generation failed: " + e.message);
        }
    }

    const generateAgain = () => {
        prev();
        onGenerateTap(prompt);
    }

    const fetchUserName = async () => {
        try {
            let newGuid = await user?.uid
            setGuid(newGuid)
            let email = await user?.email
            let dispName = await user?.displayName
            window.hj('identify', newGuid, {
                'email' : email
            });
            let resultName = !dispName ? email : dispName
            setName(resultName);
        } catch (err) {
            console.error(err);
            alert(err);
        }
    };

    useEffect(() => {
        if (loading) return;
        if (!user) {
            if (isVolumetric) {
                return navigate("/signin-3d-texturing-volumetric");
            } else {
                return navigate("/signin-3d-texturing");
            }
        }
        track("3d-texturing-volumetric-user-loaded");
        fetchToken();
        fetchUserName();
    }, [user, loading]);
    const next = () => {
        setCurrent(current + 1);
    };
    const prev = () => {
        setCurrent(current - 1);
    };

    const extractAssetDownloadUrl = async () => {
        const assetUrl = await getDownloadURL(sRef(default3DAssetsStorage, bucketPath));
        setAssetUrl(assetUrl);
    }

    useEffect(() => {
        extractAssetDownloadUrl();
    }, [bucketPath]);

    useEffect(() => {
        extractAssetDownloadUrl();
    }, []);

    let steps = []

    if (!isVolumetric && !isForRaf) {
        steps.push(
            {
                title: 'Choose Model',
                content: <ChooseModel
                    assetPath={bucketPath}
                    isVolumetric={isVolumetric}
                    guid={guid}
                    tkn={token}
                    onAssetUploaded={onAssetUploaded}
                    onAssetSelected={(newPath, rotation, camera) => {
                        setBucketPath(newPath);
                        setRotForDescription(rotation);
                        setCamForDescription(camera);

                        next();
                    }}
                />,
            }
        )
    }

    steps.push({
        title: 'Describe the texture',
        content: <TextureDescription
            isVolumetric={isVolumetric}
            isForRaf={isForRaf}
            path={assetUrl}
            onBackTap={prev}
            onGenerateTap={onGenerateTap}
            generating={generating}
            progress={progress}
            rotation={rotForDescription}
            camera={camForDescription}
        />,
    })
    steps.push({
        title: 'Results',
        content: <TexturingResults
            isVolumetric={isVolumetric}
            path={assetUrl}
            onBackTap={prev}
            prompt={prompt}
            results={results}
            generationTime={generationTime}
            generateAgain={generateAgain}
        />,
    })

    const items = steps.map((item) => ({
        key: item.title,
        title: item.title,
    }));

    return (
        <>
            <DashboardHeader name={name} token={token}/>
            <div>{steps[current].content}</div>
        </>
    );
};

export default TexturingContainer;